<template>
    <div class="inactive-page-container">
        <!-- Logo section -->
        <div class="logo-section">
            <img src="../assets/images/makeping-logo-dark.svg" alt="MakePing Logo" class="logo" />
        </div>
        
        <!-- Content section -->
        <div class="content-container">
            <!-- Main content card -->
            <div class="content-card">
            <!-- Inner content wrapper -->
            <div class="content-wrapper">
                <!-- Illustration container with padding -->
                <div class="illustration-wrapper">
                    <img 
                        :src="role === 'subUser' ? require('../assets/icons/inactive-subuser-img.svg') : require('../assets/icons/inactive-user-img.svg')" 
                        alt="Inactive Account" 
                        class="illustration" 
                    />
                </div>

                <!-- Text content -->
                <div v-if="!isRequestSent" class="text-content">
                    <h1 class="title" v-if="role === 'subUser'">Your Account Needs Activation</h1>
                    <h1 class="title" v-else>Access Restricted – Activation Needed By MakePing Team</h1>
                    
                    <p class="description" v-if="role === 'subUser'">
                        Your account is currently inactive. Please request activation from the admin to continue using your account.
                    </p>
                    <p class="description" v-else>
                        Your account is inactive. Please request activation from the MakePing Team to regain access
                    </p>
                </div>
                <div v-else class="text-content">
                    <h1 class="title">Request Sent!</h1>
                    <p class="description" v-if="role === 'subUser'">
                        Your activation request has been sent to the admin. You can send another request after 24 hours if your account is still not activated.
                    </p>
                    <p class="description" v-else>
                        Request submitted! The MakePing Team will review it soon. You may send another request after 24 hours if needed.
                    </p>
                </div>
            </div>

            <!-- Button -->
            <button 
                class="activation-button" 
                :class="{ 'disabled': isRequestSent }"
                @click="requestActivation"
                :disabled="isRequestSent"
            >
                {{ buttonText }}
            </button>

            <!-- Back to Login link -->
            <a href="#" @click.prevent="logout()" class="back-link">
                Back to Login
            </a>

            <!-- Divider line -->
            <div class="divider"></div>
        </div>
        
        <!-- Connect With Support Team link (shows when timer is active) -->
        <a v-if="isRequestSent" href="https://makeping.com/contact" target="_blank" class="support-link">
            <span class="support-link-black">Connect With</span> <span class="support-link-blue">Support Team</span>
        </a>
        </div>
    </div>
</template>

<script>
import Userauth from "../auth/js/index";

export default {
    name: "InactiveUser",
    data() {
        return {
            isRequestSent: false,
            remainingTime: '',
            timerInterval: null,
            buttonText: 'Send Activation Request',
            userid: null,
            role: null
        };
    },

    mounted() {
        // Get userid and role from localStorage
        const inactiveUserData = localStorage.getItem('inactiveUserData');
        if (inactiveUserData) {
            try {
                const data = JSON.parse(inactiveUserData);
                this.userid = data.userid || null;
                this.role = data.role || null;
            } catch (e) {
                console.error('Error parsing inactive user data:', e);
                this.userid = null;
                this.role = null;
            }
        }
        
        console.log('InactiveUser mounted with:', {
            userid: this.userid,
            role: this.role
        });
        
        // Check if there's an existing timer
        this.checkExistingTimer();
    },

    beforeDestroy() {
        // Clear interval when component is destroyed
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    },

    methods: {
        logout: function () {
            // Clear inactive user data from localStorage
            localStorage.removeItem('inactiveUserData');
            localStorage.removeItem('activationRequestTime');
            
            Userauth.Logout();
            this.$router.replace('/auth/login')
        },
        
        requestActivation: function() {
            if (this.isRequestSent) return;
            
            // Set the timer for 24 hours from now
            const expiryTime = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours in milliseconds
            localStorage.setItem('activationRequestTime', expiryTime);
            
            // Update state
            this.isRequestSent = true;
            
            // Start the timer
            this.startTimer(expiryTime);
            
            // Make the actual request (for now, redirect to contact support)
            window.open('https://makeping.com/contact', '_blank');
        },
        
        checkExistingTimer: function() {
            const expiryTime = localStorage.getItem('activationRequestTime');
            if (expiryTime) {
                const now = new Date().getTime();
                const expiry = parseInt(expiryTime);
                
                if (now < expiry) {
                    // Timer is still active
                    this.isRequestSent = true;
                    this.startTimer(expiry);
                } else {
                    // Timer has expired
                    localStorage.removeItem('activationRequestTime');
                    this.isRequestSent = false;
                }
            }
        },
        
        startTimer: function(expiryTime) {
            // Update immediately
            this.updateRemainingTime(expiryTime);
            
            // Update every second
            this.timerInterval = setInterval(() => {
                const now = new Date().getTime();
                
                if (now >= expiryTime) {
                    // Timer expired
                    clearInterval(this.timerInterval);
                    localStorage.removeItem('activationRequestTime');
                    this.isRequestSent = false;
                    this.buttonText = 'Send Activation Request';
                } else {
                    this.updateRemainingTime(expiryTime);
                }
            }, 1000);
        },
        
        updateRemainingTime: function(expiryTime) {
            const now = new Date().getTime();
            const distance = expiryTime - now;
            
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);
            
            // Format with leading zeros
            const formattedHours = String(hours).padStart(2, '0');
            const formattedMinutes = String(minutes).padStart(2, '0');
            // const formattedSeconds = String(seconds).padStart(2, '0'); // Not used in display

            this.remainingTime = `${formattedHours} hrs ${formattedMinutes} min`;
            this.buttonText = `${this.remainingTime} left`;
        }
    },
};
</script>

<style scoped>
* {
    box-sizing: border-box;
}

.inactive-page-container {
    min-height: 100vh;
    background-color: #F2F7FF;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    font-family: 'Poppins', sans-serif;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: auto;
}

.logo-section {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 30px;
    width: 100%;
}

.logo {
    height: 50px;
    width: auto;
    object-fit: contain;
}

.content-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 650px;
}

.content-card {
    background: #FFFFFF;
    border: 1px solid #D2E3FB;
    border-radius: 20px;
    padding: 30px;
    width: 650px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    position: relative;
}

.content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    width: 100%;
}

.illustration-wrapper {
    width: 350px;
    max-width: 100%;
    padding: 15px 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
}

.illustration {
    width: auto;
    height: 100%;
    max-width: 100%;
    object-fit: contain;
}

.text-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    text-align: center;
}

.title {
    font-family: 'Poppins', sans-serif;
    font-weight: 700;
    font-size: 30px;
    line-height: 1.5em;
    color: #3A3A3A;
    margin: 0;
}

.description {
    font-family: 'Poppins', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.5625em;
    color: #3A3A3A;
    margin: 0;
}

.activation-button {
    background: #0062CC;
    border: none;
    border-radius: 12px;
    padding: 10px 20px;
    width: 100%;
    height: 48px;
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 18px;
    line-height: 1.5em;
    color: #FFFFFF;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.activation-button:hover {
    background: #0056B3;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 98, 204, 0.3);
}

.activation-button:active {
    transform: translateY(0);
}

.activation-button.disabled {
    background: #7D8699;
    cursor: not-allowed;
}

.activation-button.disabled:hover {
    background: #7D8699;
    transform: none;
    box-shadow: none;
}

.back-link {
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 18px;
    line-height: 1.5em;
    color: #3A3A3A;
    text-decoration: none;
    transition: color 0.3s ease;
}

.back-link:hover {
    color: #0062CC;
}

.divider {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: #FFFFFF;
    opacity: 0.1;
}

/* Responsive adjustments */
@media (max-width: 640px) {
    .inactive-page-container {
        padding: 15px;
    }

    .logo-section {
        margin-bottom: 20px;
    }

    .logo {
        height: 40px;
    }

    .content-card {
        padding: 20px;
        gap: 20px;
    }

    .illustration-wrapper {
        width: 280px;
        padding: 10px 5px;
    }

    .title {
        font-size: 24px;
    }

    .description {
        font-size: 14px;
    }

    .activation-button,
    .back-link {
        font-size: 16px;
    }
}

/* Extra small screens */
@media (max-width: 480px) {
    .logo-section {
        margin-bottom: 15px;
    }

    .logo {
        height: 35px;
    }

    .content-card {
        padding: 15px;
        gap: 15px;
    }

    .illustration-wrapper {
        width: 240px;
    }

    .title {
        font-size: 20px;
    }
}

/* Height-based responsive adjustments */
@media (max-height: 700px) {
    .logo-section {
        margin-bottom: 20px;
    }
    
    .logo {
        height: 40px;
    }
}

@media (max-height: 600px) {
    .logo-section {
        margin-bottom: 15px;
    }
    
    .content-card {
        gap: 15px;
    }
    
    .logo {
        height: 35px;
    }
}

/* Support link styling */
.support-link {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.5em;
    text-decoration: none;
    transition: all 0.3s ease;
    margin-top: 20px;
}

.support-link-black {
    color: #3A3A3A;
}

.support-link-blue {
    color: #0062CC;
}

.support-link:hover {
    text-decoration: underline;
}

.support-link:hover .support-link-blue {
    color: #0056B3;
}

/* Responsive support link */
@media (max-width: 640px) {
    .support-link {
        font-size: 16px;
        margin-top: 15px;
    }
}
</style>